import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, RefreshCw } from "lucide-react";
import { api, errText, CURRENCIES, fmtAmount, fmtUSD2 } from "@/lib/apiClient";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const KINDS = [
  { key: "companies", label: "Компании" },
  { key: "projects", label: "Проекты" },
  { key: "categories", label: "Категории" },
  { key: "counterparties", label: "Контрагенты" },
  { key: "currencies", label: "Валюты" },
  { key: "telegram_users", label: "Telegram-доступ" },
];

export default function Dictionaries() {
  const [kind, setKind] = useState("companies");
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", currency: "USD", kind: "", description: "", opening_balance: "" });
  const [editId, setEditId] = useState(null);
  const [rates, setRates] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get(`/dict/${kind}`);
    setItems(data);
  }, [kind]);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    if (!form.name.trim()) {
      toast.error("Укажите название");
      return;
    }
    const payload = { name: form.name.trim() };
    if (kind === "companies") {
      payload.currency = form.currency;
      payload.opening_balance = Number(form.opening_balance || 0);
    }
    if (kind === "categories") payload.kind = form.kind || null;
    if (form.description) payload.description = form.description;
    try {
      if (editId) await api.put(`/dict/${kind}/${editId}`, payload);
      else await api.post(`/dict/${kind}`, payload);
      toast.success("Сохранено");
      setOpen(false);
      setEditId(null);
      load();
    } catch (e) {
      toast.error(errText(e.response?.data?.detail));
    }
  };

  const remove = async (item) => {
    try {
      await api.delete(`/dict/${kind}/${item.id}`);
      toast.success("Удалено");
      load();
    } catch (e) {
      toast.error(errText(e.response?.data?.detail));
    }
  };

  const loadRates = async () => {
    const { data } = await api.get("/fx/rates");
    setRates(data.rates);
    toast.success("Курсы обновлены (в USD за единицу валюты)");
  };

  return (
    <div className="space-y-6 md:space-y-8">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="text-3xl font-bold sm:text-4xl">Справочники</h1>
          <p className="mt-1 text-sm text-muted-foreground">Компании, проекты, категории, контрагенты и валюты</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="gap-2" data-testid="load-rates" onClick={loadRates}>
            <RefreshCw className="h-4 w-4" /> Курсы валют
          </Button>
          <Button className="gap-2" data-testid="add-dict-item"
            onClick={() => { setForm({ name: "", currency: "USD", kind: "", description: "", opening_balance: "" }); setEditId(null); setOpen(true); }}>
            <Plus className="h-4 w-4" /> Добавить
          </Button>
        </div>
      </div>

      <Tabs value={kind} onValueChange={setKind}>
        <TabsList className="flex w-full flex-wrap justify-start">
          {KINDS.map((k) => (
            <TabsTrigger key={k.key} value={k.key} data-testid={`dict-tab-${k.key}`}>{k.label}</TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {rates && (
        <Card className="glass" data-testid="rates-card">
          <CardContent className="flex flex-wrap gap-4 p-5">
            {Object.entries(rates).map(([code, v]) => (
              <div key={code} className="rounded-lg border bg-card/60 px-3 py-2">
                <div className="text-xs text-muted-foreground">{code}</div>
                <div className="font-mono text-sm font-semibold">{v} USD</div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <div className="overflow-x-auto rounded-lg border" data-testid="dict-table">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/40">
              <TableHead>Название</TableHead>
              {kind === "companies" && <TableHead>Валюта</TableHead>}
              {kind === "companies" && <TableHead className="text-right">Начальный остаток</TableHead>}
              {kind === "categories" && <TableHead>Тип</TableHead>}
              {kind === "currencies" && <TableHead>Курс к USD</TableHead>}
              <TableHead className="text-right">Действия</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((i) => (
              <TableRow key={i.id} data-testid={`dict-row-${i.id}`} className="row-anim">
                <TableCell className="font-medium">{i.name}</TableCell>
                {kind === "companies" && <TableCell className="font-mono text-sm">{i.currency || "USD"}</TableCell>}
                {kind === "companies" && (
                  <TableCell className="text-right font-mono text-sm" data-testid={`dict-opening-${i.id}`}>
                    {fmtAmount(i.opening_balance || 0, i.currency || "USD")}
                    <div className="text-[11px] text-muted-foreground">{fmtUSD2(i.opening_balance_usd || 0)}</div>
                  </TableCell>
                )}
                {kind === "categories" && (
                  <TableCell className="text-sm text-muted-foreground">
                    {i.kind === "income" ? "Доход" : i.kind === "expense" ? "Расход" : "—"}
                  </TableCell>
                )}
                {kind === "currencies" && <TableCell className="font-mono text-sm">{i.rate_to_usd ?? "—"}</TableCell>}
                <TableCell>
                  <div className="flex justify-end gap-1">
                    <Button variant="ghost" size="icon" data-testid={`edit-dict-${i.id}`}
                      onClick={() => { setForm({ name: i.name, currency: i.currency || "USD", kind: i.kind || "", description: i.description || "", opening_balance: i.opening_balance ?? "" }); setEditId(i.id); setOpen(true); }}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" data-testid={`delete-dict-${i.id}`} onClick={() => remove(i)}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {!items.length && (
              <TableRow>
                <TableCell colSpan={4} className="py-10 text-center text-sm text-muted-foreground" data-testid="dict-empty">
                  Записей нет
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="dict-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">{editId ? "Редактировать запись" : "Новая запись"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>{kind === "telegram_users" ? "Telegram ID" : "Название"}</Label>
              <Input value={form.name} data-testid="dict-name"
                placeholder={kind === "telegram_users" ? "Например: 123456789" : ""}
                onChange={(e) => setForm({ ...form, name: e.target.value })} />
              {kind === "telegram_users" && (
                <p className="text-[11px] text-muted-foreground">
                  Числовой ID пользователя Telegram. Узнать его можно командой /id в боте.
                </p>
              )}
            </div>
            {kind === "companies" && (
              <div className="space-y-2">
                <Label>Основная валюта</Label>
                <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                  <SelectTrigger data-testid="dict-currency"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CURRENCIES.map((c) => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {kind === "companies" && (
              <div className="space-y-2">
                <Label>Текущий остаток (начальный)</Label>
                <Input type="number" value={form.opening_balance} data-testid="dict-opening-balance"
                  placeholder="0"
                  onChange={(e) => setForm({ ...form, opening_balance: e.target.value })} />
                <p className="text-[11px] text-muted-foreground">
                  Сумма в валюте компании, от которой начинается отсчёт. Все операции прибавляются к этому остатку.
                </p>
              </div>
            )}

            {kind === "categories" && (
              <div className="space-y-2">
                <Label>Тип категории</Label>
                <Select value={form.kind || "none"} onValueChange={(v) => setForm({ ...form, kind: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="dict-kind"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Не указан</SelectItem>
                    <SelectItem value="income">Доход</SelectItem>
                    <SelectItem value="expense">Расход</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="dict-cancel">Отмена</Button>
            <Button onClick={save} data-testid="dict-save">Сохранить</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
